import type { NavBarConfig } from '@gm-commercial/navbar-model';
import type { Profile, Role } from '@gm-commercial/profile-model';

import { ADMIN_ROLES } from '~/common/constants/common.constant';
import type { FeatureFlagValueType } from '~/common/models/featureFlags.model';

import type { NAV_DRAWER_MESSAGES } from '../GlobalNavDrawer.messages';
import { getNotificationNavBarItems } from './account/notification.navBarItems.config';
import { getOrgNavBarItems } from './account/org.navBarItems.config';
import { getProfileNavBarItems } from './account/profile.navBarItems.config';
import { getSettingsNavBarItems } from './account/setting.navBarItems.config';
import { getSupportNavBarItems } from './account/support.navBarItems.config';

export const getAccountNavBarConfig = ({
  messages,
  profileParams,
  findFlagScope,
  hasRole,
  showTelematicsFleetNavSurfaces = true,
  hasEmerNotificationsAccess = false,
  isShopAddOnsEnabled = false,
  showFleetPrograms = true,
  showIntegrations = true,
  isNotificationAllowed = false,
}: {
  messages: typeof NAV_DRAWER_MESSAGES.accountMenu;
  profileParams: Profile['permissionsContextIds'];
  hasRole: (authorizedRoles: Role[]) => boolean;
  findFlagScope?: (flagName: string) => FeatureFlagValueType | undefined;
  showTelematicsFleetNavSurfaces?: boolean;
  /** Whether the org can set up emergency notifications (EMER flag and Protect). */
  hasEmerNotificationsAccess?: boolean;
  isShopAddOnsEnabled?: boolean;
  /** False while the organization record is loading, when there is no organization, when the lookup fails, and for non-US organizations. */
  showFleetPrograms?: boolean;
  /** False until the organization has an enrollment with at least one partner. */
  showIntegrations?: boolean;
  /** Signed-in role can open notification settings. Hidden when omitted. */
  isNotificationAllowed?: boolean;
}): NavBarConfig => {
  const accountNavItems = [
    ...getProfileNavBarItems(),
    ...(hasRole(ADMIN_ROLES)
      ? getSupportNavBarItems(findFlagScope)
      : [
          ...getOrgNavBarItems({
            profileParams,
            findFlagScope,
            hasRole,
            isShopAddOnsEnabled,
            showFleetPrograms,
            showIntegrations,
          }),
          ...(isNotificationAllowed
            ? getNotificationNavBarItems({
                profileParams,
                findFlagScope,
                showTelematicsFleetNavSurfaces,
                hasEmerNotificationsAccess,
              })
            : []),
          ...getSettingsNavBarItems(),
          ...getSupportNavBarItems(findFlagScope),
        ]),
  ];

  return {
    title: messages['common:account'],
    homePath: '/',
    items: accountNavItems,
  };
};
